import { r as registerInstance, h, a as Host, d as getElement } from './index-BntG-4Sv.js';
import { d as debounce } from './function-utils-D1ZpXQQS.js';
import { service, AuthenticationService, AuthorizationService, NavigationContextService, AuthenticationStorageService, RuntimeConfigurationContextService, EventService, ApplicationLifecycleContextService, UserPreferencesService, UserPreferencesContextService, GuideContextService, StorageKey, SubscriptionList, LocalStorageSubscriptionHandlerService, WindowService, navigate, SecurityContextService, EventName, MainMenuExtensionPoint } from '@ontotext/workbench-api';
import { T as TranslationService } from './translation.service-CyWR_ReZ.js';

const RESPONSIVE_BREAKPOINTS = {
    MD: 768};
/**
 * The width of the window in pixels at which the navbar should collapse.
 */
const WINDOW_WIDTH_FOR_COLLAPSED_NAVBAR = RESPONSIVE_BREAKPOINTS.MD;

const ontoLayoutCss = ":host{display:block;height:100%}.wb-layout{display:grid;grid-template:\"header header\" auto \"nav main\" 1fr \"nav footer\" auto/15rem minmax(200px, 1fr);min-height:100vh;margin:0;}.wb-layout.expanded{grid-template-columns:4.375rem 1fr}.wb-layout.is-embedded{grid-template-columns:1fr;grid-template-areas:\"main\";padding-top:1rem}.wb-layout.hide-navbar{grid-template:\"header\" auto \"main\" 1fr \"footer\" auto/1fr;}.wb-layout.hide-navbar.expanded{grid-template-columns:1fr}.wb-layout main .main-container{margin-left:0}.wb-layout main{grid-area:main}.wb-layout.loading main{position:relative;visibility:hidden}.wb-layout .default-slot-wrapper{display:none}.wb-layout .wb-header{grid-area:header;position:sticky;top:0;z-index:999}.wb-layout nav{grid-area:nav}.wb-layout footer{grid-area:footer}";

const OntoLayout = class {
    // ========================
    // Lifecycle methods
    // ========================
    constructor(hostRef) {
        registerInstance(this, hostRef);
        // ========================
        // Services
        // ========================
        this.authenticationService = service(AuthenticationService);
        this.authorizationService = service(AuthorizationService);
        this.navigationContextService = service(NavigationContextService);
        this.authStorageService = service(AuthenticationStorageService);
        this.runtimeConfigurationContextService = service(RuntimeConfigurationContextService);
        this.eventService = service(EventService);
        this.applicationLifecycleContextService = service(ApplicationLifecycleContextService);
        this.userPreferencesService = service(UserPreferencesService);
        this.userPreferencesContextService = service(UserPreferencesContextService);
        this.guideContextService = service(GuideContextService);
        this.fullJwtKey = `${StorageKey.GLOBAL_NAMESPACE}.${this.authStorageService.NAMESPACE}.${this.authStorageService.jwtKey}`;
        this.fullAuthenticatedKey = `${StorageKey.GLOBAL_NAMESPACE}.${this.authStorageService.NAMESPACE}.${this.authStorageService.authenticatedKey}`;
        this.authEvents = [this.fullJwtKey, this.fullAuthenticatedKey];
        this.isLowResolution = false;
        this.showHeader = this.isAuthenticatedFully();
        this.showNavbar = false;
        this.isEmbedded = false;
        this.loading = true;
        this.hideSolrDeprecationBanner = false;
        // ========================
        // Private
        // ========================
        /**
         * List of subscriptions to manage component lifecycle
         * */
        this.subscriptions = new SubscriptionList();
        this.isNavbarCollapsed = false;
        this.handleStorageChange = (event) => {
            const localStorageSubscriptionHandlerService = service(LocalStorageSubscriptionHandlerService);
            localStorageSubscriptionHandlerService.handleStorageChange(event);
            this.handleAuthChange(event.key);
        };
        this.windowResizeObserver = debounce(() => this.windowResizeHandler(), 50);
        WindowService.getWindow().addEventListener('storage', this.handleStorageChange);
    }
    componentDidRender() {
        this.windowResizeHandler();
    }
    componentDidLoad() {
        this.updateSolrDeprecationBannerVisibility();
    }
    connectedCallback() {
        this.subscribeToSecurityChanges();
        this.updateVisibility();
        this.subscribeToNavigationEnd();
        this.subscribeToRuntimeConfigurationChanges();
        this.subscribeToUserPreferencesChange();
        this.subscribeToBeforeMountRouting();
        this.subscribeToGuideChanges();
        this.loading = false;
    }
    /**
     * Lifecycle method called when the component is about to be removed from the DOM.
     * Unsubscribes from all subscriptions to prevent memory leaks.
     */
    disconnectedCallback() {
        this.subscriptions.unsubscribeAll();
    }
    render() {
        return (h(Host, { key: 'a2c03bbf9950679eaa453acbaa4a19b0b2531954', class: {
                'wb-layout': true,
                'is-embedded': this.isEmbedded,
                'hide-navbar': !this.showNavbar,
                'loading': this.loading
            } }, h("div", { key: 'abf73ea34785478a64aa8d9bfa1145ef0b4aa928', class: "default-slot-wrapper" }, h("slot", { key: 'dfadb7305e3a4c66f0420d5a6b6aeb1886d9f919', name: "default" })), !this.isEmbedded && h("div", { key: '62b265aa0d85f3f268db59887504499df5350431', class: "wb-header" }, !this.hideSolrDeprecationBanner &&
            h("onto-deprecation-banner", { key: '1f0e0f4b03891e54312c49af7338cfbfdb13fa34', class: 'onto-deprecation-banner', onCloseBanner: this.onSolrDeprecationBannerClosedHandler() }, h("span", { key: '1cb04339edac35746531faf974b2e4326487459f', slot: 'content' }, h("translate-label", { key: '6c878b7aa6e2d5f43080e228e0d09416c5c4dca9', labelKey: 'deprecation-banner.solr.content' }))), h("header", { key: 'ef2c7f171bc9106665c45edf35e5adeff1c37f82' }, this.showHeader && h("onto-header", { key: '38b45fa90a8f260f06c5e2d9a677aa59531dc5fa' }))), !this.isEmbedded && this.showNavbar &&
            h("nav", { key: '3b960854719196924a1c5f2b0878f3aac02f5bbf', class: "wb-navbar" }, h("onto-navbar", { key: '81dd034f009313391b85a962a0650f364a406eaf', ref: this.assignNavbarRef(), "navbar-collapsed": this.isLowResolution })), h("slot", { key: '58c1783a734f5c1af0dd775d98fa185a634791d6', name: "main" }, h("onto-loader", { key: '4e8ef836d44566a9d2c8770fbb2fa0acbbf9455a', loading: this.loading, targetSelector: 'main', size: 96, messageText: TranslationService.translate('common.loading') })), !this.isEmbedded &&
            h("footer", { key: '39857f329a5978cca0f725e535730eef6c4bffe9', class: "wb-footer" }, h("onto-footer", { key: '28bc198484360d99850149531e475a0e3ba1f426' }))));
    }
    // ========================
    // Event Listeners
    // ========================
    /**
     * Event listener for the navbar toggled event. The layout needs to respond properly when the navbar is toggled in
     * order to fit the content.
     * @param event
     */
    onNavbarToggled(event) {
        this.isNavbarCollapsed = event.detail.payload;
        if (this.isNavbarCollapsed) {
            this.hostElement.classList.add('expanded');
        }
        else {
            this.hostElement.classList.remove('expanded');
        }
    }
    /**
     * Event listener for window resize event. We need to handle this in order to allow the navbar to collapse when the
     * window is resized.
     */
    onResize() {
        this.windowResizeObserver();
    }
    onSolrDeprecationBannerClosedHandler() {
        return () => {
            this.userPreferencesService.dismissSolrDeprecationBanner();
        };
    }
    // ========================
    // Handlers
    // ========================
    windowResizeHandler() {
        var _a, _b, _c, _d;
        this.isLowResolution = WindowService.getWindow().innerWidth <= WINDOW_WIDTH_FOR_COLLAPSED_NAVBAR;
        const navbar = document.querySelector('.navbar-component');
        if (navbar) {
            const cookieBanner = document.querySelector('.cookie-consent-banner');
            const header = document.querySelector('.wb-header');
            const headerHeight = (_b = (_a = header === null || header === void 0 ? void 0 : header.getBoundingClientRect()) === null || _a === void 0 ? void 0 : _a.height) !== null && _b !== void 0 ? _b : 0;
            const bannerHeight = (_d = (_c = cookieBanner === null || cookieBanner === void 0 ? void 0 : cookieBanner.getBoundingClientRect()) === null || _c === void 0 ? void 0 : _c.height) !== null && _d !== void 0 ? _d : 0;
            navbar.style.height = `calc(100% - ${headerHeight}px - ${bannerHeight}px)`;
        }
        if (!this.isLowResolution && !this.isNavbarCollapsed) {
            this.hostElement.classList.remove('expanded');
        }
        else {
            this.hostElement.classList.add('expanded');
        }
    }
    handleAuthChange(changedKey) {
        var _a;
        if (!changedKey) {
            return;
        }
        if (this.authEvents.includes(changedKey)) {
            const authenticated = this.authStorageService.isAuthenticated();
            if (authenticated) {
                navigate((_a = this.navigationContextService.getPreviousRoute()) !== null && _a !== void 0 ? _a : './');
            }
            else {
                navigate('login');
            }
            WindowService.getWindow().location.reload();
        }
    }
    // ========================
    // Security & Permissions
    // ========================
    subscribeToSecurityChanges() {
        const securityContextService = service(SecurityContextService);
        this.subscriptions.add(securityContextService.onAuthenticatedUserChanged((authenticatedUser) => {
            this.authenticatedUser = authenticatedUser;
            this.updateVisibility();
        }));
        this.subscriptions.add(securityContextService.onSecurityConfigChanged((securityConfig) => {
            this.securityConfig = securityConfig;
            this.updateVisibility();
        }));
        this.subscriptions.add(this.eventService.subscribe(EventName.LOGIN, () => {
            this.setNavbarItemVisibility();
            this.updateVisibility();
        }));
        this.subscriptions.add(this.eventService.subscribe(EventName.LOGOUT, () => {
            this.setNavbarItemVisibility();
            this.updateVisibility();
            navigate('login');
        }));
    }
    subscribeToRuntimeConfigurationChanges() {
        this.subscriptions.add(this.runtimeConfigurationContextService.onRuntimeConfigurationChanged((config) => {
            var _a;
            this.isEmbedded = (_a = config === null || config === void 0 ? void 0 : config.isEmbedded) !== null && _a !== void 0 ? _a : false;
        }));
    }
    updateVisibility() {
        this.updateSolrDeprecationBannerVisibility();
        if (!this.authenticationService.isSecurityEnabled()) {
            this.showNavbar = true;
            this.showHeader = true;
        }
        else {
            this.showHeader = this.authenticationService.isAuthenticated() || this.authorizationService.hasFreeAccess();
            this.showNavbar = this.authenticationService.isAuthenticated() || this.authorizationService.hasFreeAccess();
        }
    }
    isAuthenticatedFully() {
        return !this.authenticationService.isSecurityEnabled() || this.authenticationService.isAuthenticated() || this.authorizationService.hasFreeAccess();
    }
    shouldShowMenu(role) {
        return this.isAuthenticatedFully() && this.authorizationService.hasRole(role);
    }
    // ========================
    // Navbar logic
    // ========================
    assignNavbarRef() {
        return (navbar) => {
            this.navbarRef = navbar;
            if (!this.navbarRef) {
                return;
            }
            this.navbarRef.menuItems = WindowService.getPluginRegistry().get(MainMenuExtensionPoint.NAME);
            this.setNavbarItemVisibility();
        };
    }
    setNavbarItemVisibility() {
        var _a, _b, _c;
        if (!((_a = this.navbarRef) === null || _a === void 0 ? void 0 : _a.menuItems)) {
            return;
        }
        // recursively check for children and set their shouldShow property
        const processItem = (item) => {
            var _a;
            item.shouldShow = this.shouldShowMenu(item.role);
            if ((_a = item.children) === null || _a === void 0 ? void 0 : _a.length) {
                item.children.forEach(processItem);
            }
        };
        (_c = (_b = this.navbarRef) === null || _b === void 0 ? void 0 : _b.menuItems) === null || _c === void 0 ? void 0 : _c.forEach(plugin => {
            plugin.items.forEach(processItem);
        });
        // Update the reference to trigger a re-render
        this.navbarRef.menuItems = [...this.navbarRef.menuItems];
    }
    subscribeToNavigationEnd() {
        this.subscriptions.add(this.eventService.subscribe(EventName.NAVIGATION_END, (navigationEndPayload) => {
            this.navigationContextService.updatePreviousRoute(navigationEndPayload.oldUrl);
            // NAVIGATION_END appears to happen in the same tick as BeforeMountRouting, so the loader never gets shown
            setTimeout(() => {
                this.loading = false;
            });
        }));
    }
    subscribeToBeforeMountRouting() {
        this.subscriptions.add(this.applicationLifecycleContextService.onNavigationBeforeMountRouting((payload) => {
            if (payload) {
                this.loading = true;
            }
        }));
    }
    subscribeToUserPreferencesChange() {
        this.subscriptions.add(this.userPreferencesContextService.onUserPreferencesChanged(() => {
            this.updateSolrDeprecationBannerVisibility();
        }));
    }
    subscribeToGuideChanges() {
        this.subscriptions.add(this.guideContextService.onHasRunningGuideChanged(() => this.updateSolrDeprecationBannerVisibility()));
    }
    updateSolrDeprecationBannerVisibility() {
        return this.hideSolrDeprecationBanner = this.userPreferencesService.isSolrDeprecationBannerDismissed() || this.guideContextService.hasRunningGuide();
    }
    get hostElement() { return getElement(this); }
};
OntoLayout.style = ontoLayoutCss;

export { OntoLayout as onto_layout };
